import { MovementBlock, movementBlockFromError } from "components/Modals/MovementBlocker";
import { errorText } from "hooks/useMoveItems";
import { api } from "services/api.service";
import { bulkEditError } from "utils/bulkEditError";

type MoveCheckFailure = {
  kind: string;
  id: number;
  label: string;
  message: string;
  blocker?: MovementBlock;
};

type MoveCheckResult = {
  destination: { code?: string; location?: { id?: number } };
  failures: MoveCheckFailure[];
};

export type MoveInventoryResult =
  | { status: "moved"; code: string; locationId: number | null }
  | { status: "refused"; message: string; blocker?: MovementBlock }
  | { status: "skip"; param: string; message: string }
  | { status: "error"; message: string };

// A location QR is a URL whose warehouse_location param is the code. A plain scan is the code.
export function scannedLocation(value: string): string {
  try {
    const url = new URL(value);
    return url.searchParams.get("warehouse_location") ?? value;
  } catch {
    return value;
  }
}

// Checks one asset against a destination, then moves it. A skip result asks the caller to confirm
// and call again with that flag set. Nothing is saved when the check refuses the location.
export async function moveInventoryToLocation(
  inventoryId: number,
  destination: string,
  skipActions: Record<string, boolean> = {},
): Promise<MoveInventoryResult> {
  const typed = destination.trim();
  if (!typed) return { status: "error", message: "Scan a location" };

  let check: MoveCheckResult;
  try {
    const { data } = await api.post<MoveCheckResult>("/scan/move_check", {
      destination: typed,
      inventoryIds: [inventoryId],
    });
    check = data;
  } catch (error) {
    return { status: "error", message: errorText(error, `Couldn't look up ${typed}.`) };
  }

  if (check.failures?.length > 0) {
    return {
      status: "refused",
      blocker: check.failures.find((failure) => failure.blocker)?.blocker,
      message: check.failures.map((failure) => `${failure.label}: ${failure.message}`).join(" "),
    };
  }

  const code = check.destination?.code;
  if (!code) return { status: "error", message: `Couldn't look up ${typed}.` };

  try {
    await api.put("inventories/bulk/edit", {
      inventories: [{ id: inventoryId, warehouseLocation: code, ...skipActions }],
    });
  } catch (error) {
    const blocker = movementBlockFromError(error);
    if (blocker) return { status: "refused", message: blocker.message, blocker };
    const skip = bulkEditError(error);
    if (skip.status === 422 && skip.param) {
      return { status: "skip", param: skip.param, message: skip.message ?? `Move this item to ${code} anyway?` };
    }
    return { status: "error", message: errorText(error, `Couldn't move this item to ${code}.`) };
  }

  const locationId = check.destination.location?.id ?? null;
  return { status: "moved", code, locationId };
}
